import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function NavItem({ to, icon, label, badge }) {
  return (
    <NavLink to={to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
      <i className={`ti ${icon}`} aria-hidden="true" />
      {label}
      {badge ? <span className="nav-badge">{badge}</span> : null}
    </NavLink>
  );
}

function NavSection({ id, label, children, collapsed, onToggle }) {
  return (
    <div className="nav-section">
      <div
        className="nav-label"
        onClick={() => onToggle(id)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
      >
        <span>{label}</span>
        <i className={`ti ${collapsed ? 'ti-chevron-right' : 'ti-chevron-down'}`}
          style={{ fontSize: 11, color: 'var(--text-tertiary)', transition: 'transform 0.15s' }} />
      </div>
      {!collapsed && children}
    </div>
  );
}

const STORAGE_KEY = 'sf_sidebar_collapsed';
const ALL_SECTIONS = ['overview','sales','production','inventory','procurement','planning','reports','system'];

function loadCollapsed() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  // Default: all collapsed
  return Object.fromEntries(ALL_SECTIONS.map(s => [s, true]));
}

export default function Sidebar({ open, onClose, stats }) {
  const { settings } = useAuth();
  const enableBackorders = settings?.enable_backorders !== 'false';
  const enableWorkOrders = settings?.enable_work_orders !== 'false';
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(loadCollapsed);

  // Auto-expand section containing active route
  useEffect(() => {
    const path = location.pathname;
    const sectionForPath = (p) => {
      if (p === '/dashboard' || p === '/integrations') return 'overview';
      if (p.startsWith('/sales') || p.startsWith('/b2b') || p.startsWith('/invoice') || p.startsWith('/account-statement') || p.startsWith('/backorder') || p.startsWith('/rma')) return 'sales';
      if (p.startsWith('/work')) return 'production';
      if (p.startsWith('/products') || p.startsWith('/kits') || p.startsWith('/bom') || p.startsWith('/raw') || p.startsWith('/adjust')) return 'inventory';
      if (p.startsWith('/vendor') || p.startsWith('/purchase') || p.startsWith('/receiving')) return 'procurement';
      if (p.startsWith('/forecast') || p.startsWith('/alerts')) return 'planning';
      if (p.startsWith('/reports')) return 'reports';
      if (p.startsWith('/ai') || p.startsWith('/users') || p.startsWith('/settings')) return 'system';
      return null;
    };
    const section = sectionForPath(path);
    if (section && collapsed[section]) {
      setCollapsed(prev => {
        const next = { ...prev, [section]: false };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
        return next;
      });
    }
  }, [location.pathname]); // eslint-disable-line

  const toggle = useCallback((id) => {
    setCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  return (
    <>
      {open && <div className="sidebar-overlay" onClick={onClose} />}
      <aside className={`sidebar${open ? ' open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <img src="/images/summit_logo.png" alt="Summit Fishing Equipment" />
          <div className="sidebar-tagline">
            <span className="sync-dot" />
            StockForge Inventory
          </div>
        </div>

        {/* Nav */}
        <nav>
          <NavSection id="overview" label="Overview" collapsed={collapsed.overview} onToggle={toggle}>
            <NavItem to="/dashboard" icon="ti-dashboard" label="Dashboard" />
            <NavItem to="/integrations" icon="ti-plug" label="Integrations" />
          </NavSection>

          <NavSection id="sales" label="Sales & Orders" collapsed={collapsed.sales} onToggle={toggle}>
            <NavItem to="/sales-orders" icon="ti-shopping-cart" label="Sales orders" />
            <NavItem to="/hazmat-shipping" icon="ti-truck" label="HazMat shipping" />
            <NavItem to="/b2b-orders" icon="ti-file-invoice" label="B2B orders" />
            <NavItem to="/invoices" icon="ti-receipt" label="Invoices" />
            <NavItem to="/account-statement" icon="ti-file-description" label="Account statements" />
            <NavItem to="/b2b-emails" icon="ti-mail" label="B2B Emails" />
            {enableBackorders && (
              <NavItem to="/backorders" icon="ti-clock-pause" label="Backorders"
                badge={stats?.openBackorders > 0 ? stats.openBackorders : null} />
            )}
            <NavItem to="/rma" icon="ti-arrow-back-up" label="Returns / RMA" />
          </NavSection>

          <NavSection id="production" label="Production" collapsed={collapsed.production} onToggle={toggle}>
            {enableWorkOrders && (
              <NavItem to="/work-orders" icon="ti-tool" label="Work orders" />
            )}
          </NavSection>

          <NavSection id="inventory" label="Inventory" collapsed={collapsed.inventory} onToggle={toggle}>
            <NavItem to="/products" icon="ti-package" label="Finished goods" />
            <NavItem to="/kits" icon="ti-packages" label="Bundles" />
            <NavItem to="/bom" icon="ti-list-tree" label="Bill of materials" />
            <NavItem to="/raw-materials" icon="ti-box" label="Raw materials" />
            <NavItem to="/adjustments" icon="ti-adjustments" label="Adjustments" />
          </NavSection>

          <NavSection id="procurement" label="Procurement" collapsed={collapsed.procurement} onToggle={toggle}>
            <NavItem to="/vendors" icon="ti-truck" label="Vendors" />
            <NavItem to="/purchase-orders" icon="ti-receipt" label="Purchase orders" />
            <NavItem to="/receiving" icon="ti-package-import" label="Receiving log" />
          </NavSection>

          <NavSection id="planning" label="Planning" collapsed={collapsed.planning} onToggle={toggle}>
            <NavItem to="/forecast" icon="ti-trending-up" label="Forecast" />
            <NavItem to="/alerts" icon="ti-bell" label="Alerts"
              badge={stats?.rawMaterialsCriticalCount > 0 ? stats.rawMaterialsCriticalCount : null} />
          </NavSection>

          <NavSection id="reports" label="Reports" collapsed={collapsed.reports} onToggle={toggle}>
            <NavItem to="/reports/inventory" icon="ti-clipboard-list" label="Inventory on hand" />
            <NavItem to="/reports/valuation" icon="ti-coin" label="Inventory valuation" />
            <NavItem to="/reports/margin" icon="ti-chart-bar" label="Gross margin" />
            <NavItem to="/reports/shrinkage" icon="ti-chart-area-line" label="Shrinkage" />
            <NavItem to="/reports/reorder" icon="ti-cash" label="Reorder cost" />
            <NavItem to="/reports/weekly" icon="ti-report" label="Weekly report" />
            <NavItem to="/reports/upcoming-deliveries" icon="ti-truck" label="Upcoming deliveries" />
          </NavSection>

          <NavSection id="system" label="System" collapsed={collapsed.system} onToggle={toggle}>
            <NavItem to="/ai" icon="ti-robot" label="AI admin panel" />
            <NavItem to="/users" icon="ti-users" label="Users" />
            <NavItem to="/box-sizes" icon="ti-box" label="Box Sizes" />
            <NavItem to="/settings" icon="ti-settings" label="Settings" />
          </NavSection>
        </nav>
      </aside>
    </>
  );
}
